import './BubbleTypeahead.scss';

import React, { useEffect, useState } from 'react';

import BubbleTypeahead, {
  BubbleTypeaheadOption,
  BubbleTypeaheadProps,
  BubbleTypeaheadValue,
} from './BubbleTypeahead';
import uuid from 'uuid';
import { firstOf } from '@go-shared/lib/functional';

interface BubbleTypeaheadSimplePropsBase<T> {
  options: T[] | (() => Promise<T[]>);
  value: T[];
  onCreate?: [(s: string) => T, (itm: T) => unknown];
  onTrash?: (itm: T) => void;
  onValue?: (val: T[]) => unknown;
  onOptions?: (opts: T[]) => unknown;
  keyExtractor?: (item: T) => string;
  autoAddValue?: boolean;
}

export type BubbleTypeaheadSimpleProps<T> = BubbleTypeaheadSimplePropsBase<T> &
  Omit<
    BubbleTypeaheadProps<T>,
    | keyof BubbleTypeaheadSimplePropsBase<T>
    | 'onCreate'
    | 'onAdd'
    | 'onRemove'
    | 'onTrash'
    | 'onClearAll'
  >;

function BubbleTypeaheadSimple<T extends string | number | object>(
  props: BubbleTypeaheadSimpleProps<T>
) {
  const {
    options: propOpts,
    value: propVal,
    onValue,
    onCreate,
    onOptions,
    onTrash,
    keyExtractor,
    autoAddValue,
    ...rest
  } = props;

  function internalKeyExtractor(item: T): string {
    return keyExtractor ? keyExtractor(item) : JSON.stringify(item);
  }

  const [internalOpts, setInternalOpts] = useState<Array<BubbleTypeaheadOption<T>>>([]);

  async function loadOptions() {
    const options: Array<BubbleTypeaheadOption<T>> = firstOf(
      (typeof propOpts === 'function' ? await propOpts() : propOpts).map(o => ({
        key: typeof o === 'string' || typeof o === 'number' ? `${o}` : internalKeyExtractor(o),
        item: o,
      })),
      opt => opt.key
    );
    setInternalOpts(options);
  }

  useEffect(() => {
    loadOptions().catch(console.error);
  }, [propOpts]);

  const value: Array<BubbleTypeaheadValue<T>> = propVal.map(v => ({
    key: uuid(),
    item: v,
    refKey: typeof v === 'string' || typeof v === 'number' ? `${v}` : internalKeyExtractor(v),
  }));

  function handleOnAdd(newVal: BubbleTypeaheadValue<T>) {
    if (!onValue) return;
    onValue([...propVal, newVal.item]);
  }

  function handleOnCreate(newVal: string) {
    if (!onCreate) return;
    const newItem = onCreate[0](newVal);
    onCreate[1](newItem);

    if (!onOptions) return;
    onOptions([...internalOpts.map(opt => opt.item), newItem]);

    if (!autoAddValue) return;
    setTimeout(() => {
      if (!onValue) return;
      onValue([...propVal, newItem]);
    });
  }

  function handleOnRemove(newVal: BubbleTypeaheadValue<T>) {
    if (!onValue) return;
    onValue(value.filter(v => v.key !== newVal.key).map(v => v.item));
  }

  function handleOnTrash(opt: BubbleTypeaheadOption<T>) {
    if (!onTrash) return;
    onTrash(opt.item);

    if (!onOptions) return;
    onOptions(internalOpts.filter(o => o.key !== opt.key).map(o => o.item));

    if (!autoAddValue) return;
    setTimeout(() => {
      if (!onValue) return;
      onValue(value.filter(v => v.refKey !== opt.key).map(v => v.item));
    });
  }

  const handleOnClearAll = onValue ? () => onValue([]) : undefined;

  return (
    <BubbleTypeahead
      {...rest}
      options={internalOpts}
      value={value}
      onAdd={handleOnAdd}
      onCreate={onCreate ? handleOnCreate : undefined}
      onRemove={handleOnRemove}
      onTrash={onTrash ? handleOnTrash : undefined}
      onClearAll={handleOnClearAll}
    />
  );
}

export default BubbleTypeaheadSimple;
